import { useMemo } from "react";

import moment from "moment";

import { queryKeys } from "../queryKeys";
import type { Account } from "../rest/account";
import {
  type AggregateIRAActivityResponse,
  getAggregateIRAActivity,
  getIRALimits,
  type IRALimitsResponse,
} from "../rest/ira";
import useGetCurrentOwner from "./useGetCurrentOwner";
import useRequest, { type QueryOptions } from "./useRequest";

export const useGetAggregateIRAActivity = (
  options?: QueryOptions<AggregateIRAActivityResponse>,
) => {
  return useRequest(
    queryKeys.ira.activity(),
    async () => {
      return await getAggregateIRAActivity();
    },
    options,
  );
};

export const useGetIRALimits = ({
  taxYear,
  options,
}: {
  taxYear: string;
  options?: QueryOptions<IRALimitsResponse>;
}) => {
  return useRequest(
    queryKeys.ira.limits(taxYear),
    async () => {
      return await getIRALimits(taxYear);
    },
    options,
  );
};

const getLifetimeContributionAmountsByYear = (
  aggregateIraActivity: AggregateIRAActivityResponse,
) => {
  const amounts: Record<string, number> = {};

  aggregateIraActivity.forEach(({ tax_year, total_contribution }) => {
    if (amounts[tax_year] === undefined) {
      amounts[tax_year] = 0;
    }

    amounts[tax_year] += Number(total_contribution);
  });

  return amounts;
};

const getAccountYtdContribution = (
  aggregateIRAActivity: AggregateIRAActivityResponse,
  accountId: string,
  taxYear: string,
) => {
  const result = aggregateIRAActivity.find(
    (item) => item.account_id === accountId && item.tax_year === taxYear,
  );

  return result ? Number(result.total_contribution) : 0;
};

export const useGetIraAccountOwnerSummary = ({
  currentAccount,
  taxYear,
}: {
  currentAccount: Account;
  taxYear: string;
}) => {
  if (currentAccount?.account_type !== "ira") {
    throw new Error("Cannot fetch IRA data for a non-IRA account");
  }

  const { currentOwner } = useGetCurrentOwner({
    accountId: currentAccount?.id,
  });
  const { data: aggregateIraActivity } = useGetAggregateIRAActivity();

  const { data: iraLimits } = useGetIRALimits({ taxYear });

  return useMemo<{
    ytdAccountContributionAmount: number;
    ytdOverallContributionAmount: number;
    ownerAnnualContributionLimit: number;
    ytdAccountContributionPercent: number;
    overcontributionAmount: number;
    lifetimeOverallContributionAmount: number;
    availableTaxYears: string[];
    isLoading: boolean;
  }>(() => {
    if (!currentOwner || !aggregateIraActivity || !iraLimits) {
      return {
        ytdAccountContributionAmount: 0,
        ytdOverallContributionAmount: 0,
        ownerAnnualContributionLimit: 0,
        ytdAccountContributionPercent: 0,
        overcontributionAmount: 0,
        lifetimeOverallContributionAmount: 0,
        availableTaxYears: [],
        isLoading: true,
      };
    }

    const ytdAccountContributionAmount = getAccountYtdContribution(
      aggregateIraActivity,
      currentAccount.id,
      taxYear,
    );

    const amountsByYear =
      getLifetimeContributionAmountsByYear(aggregateIraActivity);
    const ytdOverallContributionAmount = amountsByYear[taxYear] ?? 0;

    let ownerAnnualContributionLimit = Number(iraLimits?.regular_amount) ?? 0;
    const age =
      currentOwner &&
      new Date().getFullYear() - moment(currentOwner?.date_of_birth).year();
    if (age && age >= 50) {
      ownerAnnualContributionLimit += Number(iraLimits?.catchup_amount ?? 0);
    }

    return {
      ytdAccountContributionAmount,
      ytdOverallContributionAmount: amountsByYear[taxYear] ?? 0,
      ownerAnnualContributionLimit,
      ytdAccountContributionPercent: Math.floor(
        (100 * ytdAccountContributionAmount) / ownerAnnualContributionLimit,
      ),
      overcontributionAmount: Math.max(
        ytdOverallContributionAmount - ownerAnnualContributionLimit,
        0,
      ),
      lifetimeOverallContributionAmount: Object.values(amountsByYear).reduce(
        (acc, cur) => acc + cur,
        0,
      ),
      availableTaxYears: Object.keys(amountsByYear),
      isLoading: false,
    };
  }, [
    currentOwner,
    taxYear,
    aggregateIraActivity,
    iraLimits,
    currentAccount.id,
  ]);
};
